<template lang="pug">
topic(:topic="topic" :replyCount="topic.replyCount" :page.sync="page")
</template>

<script>
import gql from 'graphql-tag';
import Topic from '@/components/forum/topic.vue';

const topicBySlug = gql`
	query topicBySlug($slug: String, $page: Int!) {
		topic(slug: $slug) {
			...TopicDataFragment
		}
	}
	${Topic.fragments}
`;

export default {
	name: 'ForumTopic',
	components: { Topic },
	layout: 'forum',
	scrollToTop: true,
	nuxtI18n: {
		paths: {
			'nl-NL': '/forum/topic/:slug',
			'nl-BE': '/forum/topic/:slug'
		}
	},
	httpHeaders: async ({ app, params, query }) => {
		const topic = await app.$graphClient
			.query({
				query: gql`
					query topicBySlug($slug: String) {
						topic(slug: $slug) {
							isOldTopic
							replyCount
						}
					}
				`,
				variables: {
					page: parseInt(query.page) || 1,
					slug: params.slug
				}
			})
			.then(({ data }) => {
				return data.topic;
			})
			.catch(error => {
				if (error.message.includes('not found')) {
					return app.httpError(
						404,
						null,
						`forum-topic-not-found:${params.slug}`,
						error
					);
				}
			});

		if (topic.isOldTopic) {
			return {
				'Cache-Control':
					'public, max-age=604800, must-revalidate, s-maxage=604800, stale-while-revalidate'
			};
		} else if (
			(!query.page && topic.replyCount > 35) ||
			query.page * 30 + 5 < topic.replyCount
		) {
			return {
				'Cache-Control':
					'public, max-age=3600, must-revalidate, s-maxage=86400, stale-while-revalidate'
			};
		} else {
			return {
				'Cache-Control': 'no-cache'
			};
		}
	},
	asyncData({ app, params, query, route }) {
		const variables = {
			page: parseInt(query.page) || 1,
			slug: params.slug
		};
		return app.$graphClient
			.query({
				query: topicBySlug,
				variables,
				fetchPolicy: 'network-only'
			})
			.then(({ data }) => {
				const crumbs = {
					[app.i18n.t('page.forum:breadcrumb')]: { name: 'forum-category' }
				};

				if (data.topic.category && data.topic.category !== 'default') {
					crumbs[
						app.i18n.t(
							`page.forum:${app.$normalizeSlug(data.topic.category)}.breadcrumb`
						)
					] = {
						name: 'forum-category',
						params: {
							category: app.$localeSlug(data.topic.category, app.i18n.locale)
						}
					};
				}

				crumbs[data.topic.title] = {
					name: 'forum-topic',
					params: { slug: data.topic.slug }
				};

				let canonical = null;

				if (data.topic.destinations.length) {
					let name = '';
					const params = { slug: data.topic.slug };

					if (data.topic.destinations[0].__typename === 'Area') {
						name = 'areas-area-forum-topic';
						params.area = data.topic.destinations[0].slug;
					} else if (data.topic.destinations[0].__typename === 'Resort') {
						name = 'resorts-resort-forum-topic';
						params.resort = data.topic.destinations[0].slug;
					}

					canonical = app.localePath({ name, params, query });
				} else {
					canonical = `https://${
						app.store.state.localeDomains[app.i18n.locale]
					}${route.fullPath}`;
				}

				data.pageCount = Math.ceil(data.topic.replyCount / 30);

				data.topic.meta.pagination = {
					total: data.pageCount,
					page: variables.page
				};

				app.ads({
					sidebar: { visible: true },
					replies: { visible: true, type: 'reply' }
				});

				app.meta({
					...data.topic.meta,
					description: data.topic.content.introduction,
					canonical
				});
				app.breadcrumbs(crumbs);

				return {
					...data,
					...variables
				};
			})
			.catch(error => {
				if (error.message.includes('not found')) {
					return app.httpError(
						404,
						null,
						`forum-topic-not-found:${params.slug}`,
						error
					);
				}

				return app.httpError(500, null, 'forum-topic-01', error);
			});
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	watch: {
		page: {
			handler(value) {
				const query = value === 1 ? {} : { page: value };
				this.$router.push({ query });
			}
		}
	},
	watchQuery: ['page'],
	created() {
		this.$emit('update:showSidebar', true);
	}
};
</script>
